import React, { useState, useEffect } from 'react';
import {
  Col,
  Row,
  Form,
  Tab,
  Tabs,
  Button,
} from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppSelector } from '../reducer/reduxHooks';
import TitleBar from '../layout/PageTitle';
import QuestionList from './QuestionList';
import ModuleVideos from './ModuleVideos';
import ModuleLinks from './ModuleLinks';
import ModuleBaseEditor from './ModuleBaseEditor';
import ModuleCreateForm from './ModuleCreateForm';
import ModuleDocuments from './ModuleDocuments';

export default function ModulesAdmin() {
  const modules = useAppSelector((state) => state.moduleState.modules);
  // Show all modules in admin
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const location = useLocation();
  const [selectedModule, setSelectedModule] = useState(moduleId || '');
  const [activeTab, setActiveTab] = useState<string>('base');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (moduleId) {
      setSelectedModule(moduleId);
      // Hide create form when navigating to an existing module
      setShowCreate(false);
    }
    // Initialize activeTab from location state or query param
    const stateTab = (location.state as any)?.activeTab;
    if (stateTab && typeof stateTab === 'string') {
      setActiveTab(stateTab);
      return;
    }
    const params = new URLSearchParams(location.search);
    const queryTab = params.get('tab');
    if (queryTab) {
      setActiveTab(queryTab);
    }
  }, [moduleId, location.state, location.search]);

  const handleModuleChange = (value: string) => {
    setSelectedModule(value);
    navigate(`/admin/modules/${value}`, { state: { activeTab } });
  };

  return (
    <>
      <TitleBar pageTitle="Modul Administration" />
      <section className="section">
        <Row>
          <Col lg={3}>
            <Form>
              <Form.Group className="mb-3" controlId="moduleSelection">
                <Form.Select
                  onChange={(e) => handleModuleChange(e.target.value)}
                  value={selectedModule}
                >
                  <option value="">Modulauswahl</option>
                  {modules.map((module: Record<string, any>) => (
                    <option key={module.id} value={module.id}>{module.title}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Col>
          <Col lg={3} className="d-flex align-items-start">
            <Button
              variant={showCreate ? 'secondary' : 'primary'}
              className="mt-1"
              onClick={() => {
                // Toggle creation form; clear current selection when opening
                if (!showCreate) {
                  setSelectedModule('');
                  navigate('/admin/modules');
                }
                setShowCreate((prev) => !prev);
              }}
            >
              {showCreate ? 'Abbrechen' : 'Neues Modul'}
            </Button>
          </Col>
        </Row>
      </section>
      {showCreate && (
        <section className="section border rounded p-3 bg-light">
          <h5 className="mb-3">Neues Modul erstellen</h5>
          <ModuleCreateForm />
        </section>
      )}
      <section className="section">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'base')}
          className="mb-3"
        >
          <Tab eventKey="base" title="Overview" disabled={!selectedModule}>
            {activeTab === 'base' && selectedModule && <ModuleBaseEditor moduleId={selectedModule} />}
            {activeTab === 'base' && !selectedModule && <div className="text-muted">Bitte ein bestehendes Modul auswählen oder Neues Modul klicken.</div>}
          </Tab>
          <Tab eventKey="questions" title="Fragen" disabled={!selectedModule}>
            {activeTab === 'questions' && selectedModule && <QuestionList moduleId={selectedModule} />}
          </Tab>
          <Tab eventKey="videos" title="Videos" disabled={!selectedModule}>
            {activeTab === 'videos' && selectedModule && <ModuleVideos moduleId={selectedModule} />}
          </Tab>
          <Tab eventKey="links" title="Links" disabled={!selectedModule}>
            {activeTab === 'links' && selectedModule && <ModuleLinks moduleId={selectedModule} />}
          </Tab>
          <Tab eventKey="documents" title="Documents" disabled={!selectedModule}>
            {activeTab === 'documents' && selectedModule && <ModuleDocuments moduleId={selectedModule} />}
          </Tab>
        </Tabs>
      </section>
    </>
  );
}
